import { useEffect, useState } from 'react';
import axios from 'axios';

// Shows the user's most recent (un-undone) CSV import for this record type
// within the last 24h, with a one-click Undo. Undo skips records that have
// been modified since import (notes/documents added, lead converted, or the
// auto-created subscription customized). Rendered atop the Leads/Customers
// list pages. `onUndone` lets the page reload after a successful undo.
export default function RecentImportBanner({ recordType, onUndone }) {
  const [batch, setBatch] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    axios.get(`/api/imports/last-recent?record_type=${recordType}`)
      .then((r) => setBatch(r.data?.batch || null))
      .catch(() => setBatch(null));
  }, [recordType]);

  if (!batch) {
    if (result) {
      return (
        <div className="recent-import-banner recent-import-banner-done">
          <span>✓ Undone — deleted {result.deleted}{result.skipped > 0 && `, skipped ${result.skipped}`} of {result.total}.</span>
          <button className="btn-text" onClick={() => setResult(null)}>Dismiss</button>
        </div>
      );
    }
    return null;
  }

  const minsAgo = batch.minutes_since;
  const relTime = minsAgo < 1 ? 'just now'
                : minsAgo < 60 ? `${minsAgo} mins ago`
                : minsAgo < 1440 ? `${Math.floor(minsAgo / 60)} hrs ago`
                : 'yesterday';

  async function handleUndo() {
    if (!window.confirm(
      `Undo this import? Will attempt to delete ${batch.record_count} ${recordType}(s).\n\n` +
      `Records that have been modified (notes added, quotes sent, etc.) will be skipped.`
    )) return;
    setBusy(true);
    try {
      const r = await axios.post(`/api/imports/${batch.id}/undo`);
      setResult(r.data);
      setBatch(null);
      if (onUndone) onUndone();
    } catch (e) {
      alert(`Undo failed: ${e.response?.data?.error || e.message}`);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="recent-import-banner">
      <span>
        Last import: <strong>{batch.record_count}</strong> {recordType}{batch.record_count === 1 ? '' : 's'} {relTime}
        {batch.user_name && ` by ${batch.user_name}`}
        {batch.source_filename && <span className="cell-mono"> · {batch.source_filename}</span>}
      </span>
      <button className="btn-secondary btn-sm" onClick={handleUndo} disabled={busy}>
        {busy ? 'Undoing…' : 'Undo'}
      </button>
    </div>
  );
}
